import React, { useEffect, useState } from "react";
import { X, FloppyDisk, Trash, Sparkle, Spinner, EnvelopeSimple, LinkedinLogo, Copy, NotePencil, Gauge, PaperPlaneTilt, Star, GoogleLogo, CreditCard, ClockCounterClockwise, PencilSimple, ArrowClockwise, Check } from "@phosphor-icons/react";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScoreBadge } from "@/components/ScoreBadge";
import ProposalsTab from "@/components/ProposalsTab";
import HistoryTab from "@/components/HistoryTab";
import AuditAnalyticsCard from "@/components/AuditAnalyticsCard";
import ConfirmDialog from "@/components/ConfirmDialog";
import { toast } from "sonner";

const EMPTY_LEAD = {
  business_name: "",
  industry: "",
  city: "",
  state: "",
  website: "",
  owner_name: "",
  email: "",
  phone: "",
  linkedin_url: "",
  google_business_url: "",
  website_quality_score: 0,
  seo_score: 0,
  review_score: 0,
  lead_score: 0,
  pain_points: "",
  recommended_offer: "",
  status: "New Lead",
  next_follow_up_date: "",
  notes: "",
  audit_report: "",
  outreach_email: "",
  outreach_linkedin: "",
};

export default function LeadDrawer({ leadId, stages, onClose, onUpdated, onCreated, onDeleted }) {
  const isNew = leadId === "__new__";
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [auditing, setAuditing] = useState(false);
  const [auditStream, setAuditStream] = useState("");
  const [outreachLoading, setOutreachLoading] = useState(false);
  const [psiLoading, setPsiLoading] = useState(false);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsStatus, setReviewsStatus] = useState({ google_configured: false, yelp_configured: false });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [stripeConfigured, setStripeConfigured] = useState(false);
  const [tab, setTab] = useState("overview");
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Editable outreach draft (subject + body)
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailDraft, setEmailDraft] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [enriching, setEnriching] = useState(false);

  useEffect(() => {
    if (!leadId) return;
    api.get("/email/status").then((r) => setEmailConfigured(!!r.data.configured)).catch(() => {});
    api.get("/reviews/status").then((r) => setReviewsStatus(r.data)).catch(() => {});
    api.get("/payments/status").then((r) => setStripeConfigured(!!r.data.configured)).catch(() => {});
    if (isNew) {
      setLead({ ...EMPTY_LEAD });
      setTab("overview");
      return;
    }
    setLoading(true);
    api.get(`/leads/${leadId}`)
      .then((r) => {
        setLead(r.data);
        setAuditStream(r.data.audit_report || "");
        setTab("overview");
      })
      .catch(() => toast.error("Lead not found"))
      .finally(() => setLoading(false));
  }, [leadId, isNew]);

  if (!leadId) return null;

  const set = (k, v) => setLead((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) {
        if (!lead.business_name) {
          toast.error("Business name required");
          setSaving(false);
          return;
        }
        const { data } = await api.post("/leads", lead);
        onCreated?.(data);
        toast.success("Lead created");
        onClose?.();
      } else {
        const { data } = await api.patch(`/leads/${lead.id}`, lead);
        setLead(data);
        onUpdated?.(data);
        toast.success("Saved");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const apolloEnrich = async () => {
    if (!lead?.id || isNew) return;
    setEnriching(true);
    try {
      const { data } = await api.post("/hunter/enrich", { lead_id: lead.id });
      if (data.skipped) {
        toast.message("Hunter.io not configured", {
          description: "Admin needs to add HUNTER_API_KEY. Free signup at hunter.io (25 searches/mo).",
        });
      } else if (!data.matched) {
        toast.message("No Hunter match", { description: data.reason });
      } else if (!data.updated) {
        toast.message("No new data", { description: data.reason });
      } else {
        toast.success(`Hunter enriched ${data.field_count} field(s)`, {
          description: `${data.emails_found || 0} emails found at ${data.domain}`,
        });
        setLead(data.lead);
        onUpdated?.(data.lead);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Hunter enrichment failed");
    } finally {
      setEnriching(false);
    }
  };

  const del = async () => {
    setDeleteOpen(false);
    try {
      await api.delete(`/leads/${lead.id}`);
      onDeleted?.(lead.id);
      toast.success("Deleted");
      onClose?.();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const runAudit = async () => {
    if (isNew) {
      toast.error("Save lead first");
      return;
    }
    setAuditing(true);
    setAuditStream("");
    setTab("audit");
    try {
      const token = localStorage.getItem("cf_token");
      const res = await fetch(`${API_BASE}/ai/audit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ lead_id: lead.id }),
      });
      if (!res.ok || !res.body) {
        throw new Error(`HTTP ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        setAuditStream(buf);
      }
      setLead((p) => ({ ...p, audit_report: buf }));
      onUpdated?.({ ...lead, audit_report: buf });
      toast.success("Audit ready");
    } catch (err) {
      toast.error("Audit failed: " + err.message);
    } finally {
      setAuditing(false);
    }
  };

  const runOutreach = async () => {
    if (isNew) {
      toast.error("Save lead first");
      return;
    }
    setOutreachLoading(true);
    setTab("outreach");
    try {
      const { data } = await api.post("/ai/outreach", { lead_id: lead.id });
      const newLead = {
        ...lead,
        outreach_email: `Subject: ${data.email_subject}\n\n${data.email_body}`,
        outreach_linkedin: `--- Connection ---\n${data.linkedin_connect}\n\n--- Follow-up ---\n${data.linkedin_followup}`,
      };
      setLead(newLead);
      onUpdated?.(newLead);
      toast.success("Outreach generated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Outreach failed");
    } finally {
      setOutreachLoading(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text || "");
    toast.success(`Copied ${label}`);
  };

  const mailto = () => {
    if (!lead.email) {
      toast.error("Lead has no email");
      return;
    }
    const subjectMatch = (lead.outreach_email || "").match(/^Subject:\s*(.*)$/m);
    const subject = subjectMatch ? subjectMatch[1] : "Quick question for " + lead.business_name;
    const body = (lead.outreach_email || "").replace(/^Subject:.*\n*/m, "");
    window.open(`mailto:${lead.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  };

  const startEditEmail = () => {
    setEmailDraft(lead.outreach_email || "Subject: \n\n");
    setEditingEmail(true);
  };
  const cancelEditEmail = () => {
    setEditingEmail(false);
    setEmailDraft("");
  };
  const resetToAiDraft = () => {
    if (!lead.outreach_email_original) {
      toast.error("No AI draft on file — click Generate first");
      return;
    }
    setEmailDraft(lead.outreach_email_original);
    toast.success("Restored the AI-generated draft");
  };
  const saveEmailDraft = async () => {
    if (isNew) { toast.error("Save lead first"); return; }
    const trimmed = (emailDraft || "").trim();
    if (!trimmed) { toast.error("Email cannot be empty"); return; }
    // Enforce the canonical "Subject: ...\n\n{body}" shape so send-outreach parses it
    let payload = trimmed;
    if (!/^Subject:\s*/i.test(payload)) {
      payload = `Subject: Quick question for ${lead.business_name}\n\n${payload}`;
    }
    setSavingEmail(true);
    try {
      const { data } = await api.patch(`/leads/${lead.id}`, { outreach_email: payload });
      setLead(data);
      onUpdated?.(data);
      setEditingEmail(false);
      toast.success("Draft saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Could not save draft");
    } finally {
      setSavingEmail(false);
    }
  };

  const runPagespeed = async () => {
    if (isNew) { toast.error("Save lead first"); return; }
    if (!lead.website) { toast.error("Add a website URL first"); return; }
    setPsiLoading(true);
    try {
      const { data } = await api.post("/pagespeed/check", { lead_id: lead.id });
      setLead(data.lead);
      onUpdated?.(data.lead);
      toast.success(`PageSpeed: perf ${data.performance} · SEO ${data.seo}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "PageSpeed failed");
    } finally {
      setPsiLoading(false);
    }
  };

  const runReviews = async () => {
    if (isNew) { toast.error("Save lead first"); return; }
    const anyConfigured = reviewsStatus.google_configured || reviewsStatus.yelp_configured;
    if (!anyConfigured) {
      toast.error("Add GOOGLE_PLACES_API_KEY and/or YELP_API_KEY to backend/.env first.");
      return;
    }
    setReviewsLoading(true);
    try {
      const { data } = await api.post("/reviews/check", { lead_id: lead.id, providers: ["google", "yelp"] });
      setLead(data.lead);
      onUpdated?.(data.lead);
      const bits = [];
      if (data.google) bits.push(`G ${data.google.rating ?? "—"}★ (${data.google.review_count ?? 0})`);
      if (data.yelp) bits.push(`Y ${data.yelp.rating ?? "—"}★ (${data.yelp.review_count ?? 0})`);
      toast.success(`Reviews: ${bits.join(" · ") || "no match"} → score ${data.review_score}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Reviews check failed");
    } finally {
      setReviewsLoading(false);
    }
  };

  const sendEmail = async () => {
    setSendConfirmOpen(false);
    if (isNew) { toast.error("Save lead first"); return; }
    if (!emailConfigured) {
      toast.error("Resend not configured. Add RESEND_API_KEY in backend/.env.");
      return;
    }
    if (!lead.email) { toast.error("Lead has no email"); return; }
    if (!lead.outreach_email) { toast.error("Generate outreach first"); return; }
    setSendingEmail(true);
    try {
      const { data } = await api.post("/email/send-outreach", { lead_id: lead.id, advance_stage: true });
      const updated = { ...lead, last_outreach_sent_at: data.sent_at, status: data.new_status };
      setLead(updated);
      onUpdated?.(updated);
      toast.success(`Sent to ${data.sent_to}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Send failed");
    } finally {
      setSendingEmail(false);
    }
  };

  const requestSendEmail = () => {
    if (isNew) { toast.error("Save lead first"); return; }
    if (!emailConfigured) {
      toast.error("Resend not configured. Add RESEND_API_KEY in backend/.env.");
      return;
    }
    if (!lead.email) { toast.error("Lead has no email"); return; }
    if (!lead.outreach_email) { toast.error("Generate outreach first"); return; }
    setSendConfirmOpen(true);
  };

  return (
    <>
      <div
        data-testid="drawer-overlay"
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 animate-in fade-in"
        onClick={onClose}
      />
      <aside
        data-testid="lead-drawer"
        className="fixed right-0 top-0 h-full w-full max-w-2xl bg-zinc-950 border-l border-zinc-800 z-50 overflow-y-auto flex flex-col"
      >
        <div className="sticky top-0 z-10 bg-zinc-950 border-b border-zinc-800 px-3 md:px-5 py-4 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-orange-500">
              {isNew ? "New lead" : "Lead detail"}
            </div>
            <div className="font-display text-lg font-semibold text-zinc-50 truncate">
              {lead?.business_name || (isNew ? "Untitled" : "—")}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isNew && <ScoreBadge score={lead?.lead_score || 0} testid="drawer-score-badge" />}
            <button
              data-testid="drawer-close-btn"
              onClick={onClose}
              className="p-2 hover:bg-zinc-900 rounded-sm text-zinc-400 hover:text-zinc-100"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {loading || !lead ? (
          <div className="p-10 text-center text-sm text-zinc-500 font-mono">Loading…</div>
        ) : (
          <>
            <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col">
              <div className="px-3 md:px-5 pt-4 overflow-x-auto">
                <TabsList className="bg-zinc-900 border border-zinc-800 rounded-sm w-max min-w-full">
                  <TabsTrigger data-testid="tab-overview" value="overview" className="data-[state=active]:bg-orange-600/20 data-[state=active]:text-orange-300 rounded-sm">
                    <NotePencil size={14} className="mr-1.5" /> Overview
                  </TabsTrigger>
                  <TabsTrigger data-testid="tab-audit" value="audit" className="data-[state=active]:bg-orange-600/20 data-[state=active]:text-orange-300 rounded-sm">
                    <Sparkle size={14} className="mr-1.5" /> AI Audit
                  </TabsTrigger>
                  <TabsTrigger data-testid="tab-outreach" value="outreach" className="data-[state=active]:bg-orange-600/20 data-[state=active]:text-orange-300 rounded-sm">
                    <EnvelopeSimple size={14} className="mr-1.5" /> Outreach
                  </TabsTrigger>
                  <TabsTrigger data-testid="tab-proposals" value="proposals" className="data-[state=active]:bg-orange-600/20 data-[state=active]:text-orange-300 rounded-sm">
                    <CreditCard size={14} className="mr-1.5" /> Proposals
                  </TabsTrigger>
                  <TabsTrigger data-testid="tab-history" value="history" className="data-[state=active]:bg-orange-600/20 data-[state=active]:text-orange-300 rounded-sm">
                    <ClockCounterClockwise size={14} className="mr-1.5" /> History
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="overview" className="flex-1 px-3 md:px-5 py-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Business Name *" testid="field-business-name">
                    <Input data-testid="input-business-name" value={lead.business_name || ""} onChange={(e) => set("business_name", e.target.value)} className="bg-zinc-900 border-zinc-800" />
                  </Field>
                  <Field label="Industry">
                    <Input data-testid="input-industry" value={lead.industry || ""} onChange={(e) => set("industry", e.target.value)} className="bg-zinc-900 border-zinc-800" />
                  </Field>
                  <Field label="City">
                    <Input data-testid="input-city" value={lead.city || ""} onChange={(e) => set("city", e.target.value)} className="bg-zinc-900 border-zinc-800" />
                  </Field>
                  <Field label="State">
                    <Input data-testid="input-state" value={lead.state || ""} onChange={(e) => set("state", e.target.value)} className="bg-zinc-900 border-zinc-800" />
                  </Field>
                  <Field label="Website" wide>
                    <div className="flex gap-2">
                      <Input data-testid="input-website" value={lead.website || ""} onChange={(e) => set("website", e.target.value)} className="bg-zinc-900 border-zinc-800 flex-1" placeholder="https://" />
                      <Button
                        type="button"
                        data-testid="pagespeed-check-btn"
                        onClick={runPagespeed}
                        disabled={psiLoading || isNew || !lead.website}
                        variant="outline"
                        className="border-zinc-800 hover:bg-zinc-900 text-zinc-200 rounded-sm whitespace-nowrap"
                        title="Run Google PageSpeed Insights"
                      >
                        {psiLoading ? <Spinner size={14} className="animate-spin" /> : <><Gauge size={14} className="mr-1.5" /> PageSpeed</>}
                      </Button>
                    </div>
                    {lead.pagespeed_checked_at && (
                      <div className="text-[10px] font-mono text-zinc-500 mt-1" data-testid="pagespeed-checked-at">
                        Last PSI check: {new Date(lead.pagespeed_checked_at).toLocaleString()}
                      </div>
                    )}
                  </Field>
                  <Field label="Owner / Decision Maker">
                    <Input data-testid="input-owner" value={lead.owner_name || ""} onChange={(e) => set("owner_name", e.target.value)} className="bg-zinc-900 border-zinc-800" />
                  </Field>
                  <Field label="Email">
                    <Input data-testid="input-email" type="email" value={lead.email || ""} onChange={(e) => set("email", e.target.value)} className="bg-zinc-900 border-zinc-800" />
                  </Field>
                  <Field label="Phone">
                    <Input data-testid="input-phone" value={lead.phone || ""} onChange={(e) => set("phone", e.target.value)} className="bg-zinc-900 border-zinc-800" />
                  </Field>
                  <Field label="LinkedIn URL">
                    <div className="flex flex-wrap gap-2">
                      <Input data-testid="input-linkedin" value={lead.linkedin_url || ""} onChange={(e) => set("linkedin_url", e.target.value)} className="bg-zinc-900 border-zinc-800 flex-1 min-w-[140px]" placeholder="https://linkedin.com/in/…" />
                      <Button
                        type="button"
                        size="sm"
                        data-testid="linkedin-find-owner-btn"
                        variant="outline"
                        onClick={() => {
                          const q = encodeURIComponent(`owner OR founder OR CEO ${lead.business_name || ""} ${lead.city || ""}`.trim());
                          window.open(`https://www.linkedin.com/search/results/people/?keywords=${q}`, "_blank", "noopener");
                        }}
                        disabled={!lead.business_name}
                        className="border-zinc-800 hover:bg-zinc-900 text-zinc-200 rounded-sm whitespace-nowrap"
                        title="Open LinkedIn People Search for this business owner (free)"
                      >
                        <LinkedinLogo size={14} className="mr-1" weight="fill" /> Find owner
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        data-testid="linkedin-find-company-btn"
                        variant="outline"
                        onClick={() => {
                          const q = encodeURIComponent(`${lead.business_name || ""} ${lead.city || ""}`.trim());
                          window.open(`https://www.linkedin.com/search/results/companies/?keywords=${q}`, "_blank", "noopener");
                        }}
                        disabled={!lead.business_name}
                        className="border-zinc-800 hover:bg-zinc-900 text-zinc-200 rounded-sm whitespace-nowrap"
                        title="Open LinkedIn Company Search (free)"
                      >
                        <LinkedinLogo size={14} className="mr-1" /> Company
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        data-testid="hunter-enrich-btn"
                        variant="outline"
                        onClick={apolloEnrich}
                        disabled={!lead.website || isNew || enriching}
                        className="border-orange-600/40 bg-orange-600/5 hover:bg-orange-600/15 text-orange-300 rounded-sm whitespace-nowrap"
                        title="Auto-find owner name + email via Hunter.io (free 25/mo with key). Requires website URL."
                      >
                        {enriching ? <Spinner size={12} className="mr-1 animate-spin" /> : <Sparkle size={12} className="mr-1" weight="fill" />}
                        Hunter
                      </Button>
                    </div>
                    <div className="text-[10px] font-mono text-zinc-500 mt-1">
                      Free LinkedIn search · Hunter auto-fills owner/email from website domain (admin needs key)
                    </div>
                  </Field>
                  <Field label="Google Business URL" wide>
                    <Input data-testid="input-gbp" value={lead.google_business_url || ""} onChange={(e) => set("google_business_url", e.target.value)} className="bg-zinc-900 border-zinc-800" />
                  </Field>

                  <Field label="Website Quality (0-100)">
                    <Input data-testid="input-wq" type="number" min={0} max={100} value={lead.website_quality_score || 0} onChange={(e) => set("website_quality_score", Number(e.target.value))} className="bg-zinc-900 border-zinc-800" />
                  </Field>
                  <Field label="SEO Score (0-100)">
                    <Input data-testid="input-seo" type="number" min={0} max={100} value={lead.seo_score || 0} onChange={(e) => set("seo_score", Number(e.target.value))} className="bg-zinc-900 border-zinc-800" />
                  </Field>
                  <Field label="Review Score (0-100)">
                    <div className="flex gap-2">
                      <Input data-testid="input-review" type="number" min={0} max={100} value={lead.review_score || 0} onChange={(e) => set("review_score", Number(e.target.value))} className="bg-zinc-900 border-zinc-800 flex-1" />
                      <Button
                        type="button"
                        size="sm"
                        data-testid="reviews-check-btn"
                        onClick={runReviews}
                        disabled={reviewsLoading || isNew}
                        variant="outline"
                        className="border-zinc-800 hover:bg-zinc-900 text-zinc-200 rounded-sm whitespace-nowrap"
                        title="Check Google + Yelp reviews"
                      >
                        {reviewsLoading ? <Spinner size={14} className="animate-spin" /> : <><Star size={14} className="mr-1" weight="fill" /> Reviews</>}
                      </Button>
                    </div>
                    {(lead.google_rating || lead.yelp_rating) && (
                      <div className="text-[10px] font-mono text-zinc-500 mt-1 flex gap-3 flex-wrap" data-testid="reviews-detail">
                        {lead.google_rating != null && (
                          <span className="inline-flex items-center gap-1" data-testid="reviews-google">
                            <GoogleLogo size={10} weight="bold" className="text-blue-400" />
                            <strong className="text-zinc-300">{lead.google_rating}★</strong> ({lead.google_review_count || 0})
                          </span>
                        )}
                        {lead.yelp_rating != null && (
                          <span className="inline-flex items-center gap-1" data-testid="reviews-yelp">
                            <span className="text-red-500 font-bold text-[9px]">Y</span>
                            <strong className="text-zinc-300">{lead.yelp_rating}★</strong> ({lead.yelp_review_count || 0})
                          </span>
                        )}
                        {lead.reviews_checked_at && (
                          <span>checked {new Date(lead.reviews_checked_at).toLocaleDateString()}</span>
                        )}
                      </div>
                    )}
                  </Field>
                  <Field label="Lead Score (auto)">
                    <Input data-testid="input-lead-score" type="number" value={lead.lead_score || 0} disabled className="bg-zinc-900 border-zinc-800 opacity-70" />
                  </Field>

                  <Field label="Stage" wide>
                    <Select value={lead.status} onValueChange={(v) => set("status", v)}>
                      <SelectTrigger data-testid="input-status" className="bg-zinc-900 border-zinc-800 text-zinc-100"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-zinc-950 border-zinc-800">
                        {stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="Pain Points" wide>
                    <Textarea data-testid="input-pain" rows={2} value={lead.pain_points || ""} onChange={(e) => set("pain_points", e.target.value)} className="bg-zinc-900 border-zinc-800" />
                  </Field>
                  <Field label="Recommended Offer" wide>
                    <Textarea data-testid="input-offer" rows={2} value={lead.recommended_offer || ""} onChange={(e) => set("recommended_offer", e.target.value)} className="bg-zinc-900 border-zinc-800" />
                  </Field>
                  <Field label="Next Follow-Up Date">
                    <Input data-testid="input-followup" type="date" value={lead.next_follow_up_date || ""} onChange={(e) => set("next_follow_up_date", e.target.value)} className="bg-zinc-900 border-zinc-800" />
                  </Field>
                  <Field label="Notes" wide>
                    <Textarea data-testid="input-notes" rows={3} value={lead.notes || ""} onChange={(e) => set("notes", e.target.value)} className="bg-zinc-900 border-zinc-800" />
                  </Field>
                </div>
              </TabsContent>

              <TabsContent value="audit" className="flex-1 px-3 md:px-5 py-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-display font-medium text-zinc-100">Free Digital Presence Audit</div>
                    <div className="text-xs text-zinc-500 mt-0.5">Streamed live from Claude Sonnet 4.5</div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      data-testid="audit-run-btn"
                      onClick={runAudit}
                      disabled={auditing || isNew}
                      className="bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm"
                    >
                      {auditing ? (<><Spinner size={14} className="mr-1.5 animate-spin" /> Forging…</>) : (<><Sparkle size={14} className="mr-1.5" weight="fill" /> {lead.audit_report ? "Regenerate" : "Generate"}</>)}
                    </Button>
                    {auditStream && (
                      <Button
                        data-testid="audit-copy-btn"
                        variant="outline"
                        onClick={() => copy(auditStream, "audit")}
                        className="border-zinc-800 hover:bg-zinc-900 rounded-sm"
                      >
                        <Copy size={14} className="mr-1.5" /> Copy
                      </Button>
                    )}
                  </div>
                </div>

                <div
                  data-testid="audit-output"
                  className={`bg-zinc-900/60 border border-zinc-800 rounded-sm p-4 min-h-[300px] whitespace-pre-wrap font-mono text-xs text-zinc-300 leading-relaxed ${auditing ? "ai-beam" : ""}`}
                >
                  {auditStream || (
                    <div className="text-zinc-600 text-center py-10">No audit yet. Click Generate to forge a report.</div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="outreach" className="flex-1 px-3 md:px-5 py-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-display font-medium text-zinc-100">Outreach Messages</div>
                    <div className="text-xs text-zinc-500 mt-0.5">Email + LinkedIn copy tailored to this lead</div>
                  </div>
                  <Button
                    data-testid="outreach-run-btn"
                    onClick={runOutreach}
                    disabled={outreachLoading || isNew}
                    className="bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm"
                  >
                    {outreachLoading ? (<><Spinner size={14} className="mr-1.5 animate-spin" /> Drafting…</>) : (<><Sparkle size={14} className="mr-1.5" weight="fill" /> {lead.outreach_email ? "Regenerate" : "Generate"}</>)}
                  </Button>
                </div>

                <div className={`bg-zinc-900/60 border border-zinc-800 rounded-sm ${outreachLoading ? "ai-beam" : ""}`}>
                  <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800 flex-wrap gap-1.5">
                    <div className="flex items-center gap-2 text-xs text-zinc-300"><EnvelopeSimple size={14} /> Email</div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {!editingEmail && (
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid="outreach-email-edit"
                          onClick={startEditEmail}
                          className="h-7 text-zinc-400 hover:text-zinc-100"
                        >
                          <PencilSimple size={12} className="mr-1" /> Edit
                        </Button>
                      )}
                      {editingEmail && lead.outreach_email_original && (
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid="outreach-email-reset"
                          onClick={resetToAiDraft}
                          className="h-7 text-zinc-400 hover:text-orange-300"
                          title="Restore the AI-generated draft"
                        >
                          <ArrowClockwise size={12} className="mr-1" /> Reset to AI
                        </Button>
                      )}
                      {editingEmail && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            data-testid="outreach-email-cancel"
                            onClick={cancelEditEmail}
                            className="h-7 text-zinc-400 hover:text-zinc-100"
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            data-testid="outreach-email-save"
                            onClick={saveEmailDraft}
                            disabled={savingEmail}
                            className="h-7 bg-emerald-600 hover:bg-emerald-500 text-zinc-950 rounded-sm"
                          >
                            {savingEmail ? <Spinner size={12} className="animate-spin mr-1" /> : <Check size={12} className="mr-1" weight="bold" />}
                            Save
                          </Button>
                        </>
                      )}
                      {!editingEmail && (
                        <>
                          <Button size="sm" variant="ghost" data-testid="outreach-email-copy" onClick={() => copy(lead.outreach_email, "email")} className="h-7 text-zinc-400 hover:text-zinc-100"><Copy size={12} className="mr-1" /> Copy</Button>
                          <Button size="sm" variant="ghost" data-testid="outreach-email-mailto" onClick={mailto} className="h-7 text-zinc-300 hover:text-zinc-100"><EnvelopeSimple size={12} className="mr-1" /> Mailto</Button>
                          <Button
                            size="sm"
                            data-testid="outreach-send-btn"
                            onClick={requestSendEmail}
                            disabled={sendingEmail || !emailConfigured || !lead.outreach_email}
                            className="h-7 bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm"
                            title={emailConfigured ? "Send via Resend" : "Configure RESEND_API_KEY in .env"}
                          >
                            {sendingEmail ? <Spinner size={12} className="animate-spin mr-1" /> : <PaperPlaneTilt size={12} className="mr-1" weight="fill" />}
                            Send
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {editingEmail ? (
                    <div className="p-3">
                      <Textarea
                        data-testid="outreach-email-textarea"
                        value={emailDraft}
                        onChange={(e) => setEmailDraft(e.target.value)}
                        rows={14}
                        className="w-full bg-zinc-950 border-zinc-800 font-mono text-xs text-zinc-200 leading-relaxed resize-y"
                        placeholder={"Subject: Your subject here\n\nWrite the email body here…"}
                      />
                      <div className="text-[10px] font-mono text-zinc-500 mt-2">
                        Line 1 must start with <span className="text-orange-400">Subject:</span> — the app auto-adds it if you forget. The audit link (when available) is appended automatically at send time.
                      </div>
                    </div>
                  ) : (
                    <pre data-testid="outreach-email-text" className="p-3 text-xs text-zinc-300 whitespace-pre-wrap font-mono min-h-[120px]">
                      {lead.outreach_email || <span className="text-zinc-600">No email drafted yet.</span>}
                    </pre>
                  )}
                  {lead.audit_share_token && lead.audit_report && !editingEmail && (
                    <div className="px-3 py-2 border-t border-zinc-800 bg-orange-500/5 text-[10px] font-mono text-orange-300 flex items-center gap-1.5" data-testid="audit-link-note">
                      <Sparkle size={10} weight="fill" /> A public audit link will be attached to this email automatically.
                    </div>
                  )}
                  {lead.last_outreach_sent_at && (
                    <div className="px-3 py-2 border-t border-zinc-800 bg-emerald-500/5 text-[10px] font-mono text-emerald-400 flex flex-wrap gap-x-3 gap-y-1" data-testid="outreach-sent-status">
                      <span>✓ Sent {new Date(lead.last_outreach_sent_at).toLocaleString()}</span>
                      {lead.last_outreach_delivered_at && <span data-testid="engagement-delivered" className="text-zinc-400">· delivered</span>}
                      {lead.last_outreach_opened_at && <span data-testid="engagement-opened" className="text-amber-300">· opened {new Date(lead.last_outreach_opened_at).toLocaleDateString()}</span>}
                      {lead.last_outreach_clicked_at && <span data-testid="engagement-clicked" className="text-orange-300">· clicked {new Date(lead.last_outreach_clicked_at).toLocaleDateString()}</span>}
                      {lead.last_outreach_bounced_at && <span data-testid="engagement-bounced" className="text-red-400">· BOUNCED</span>}
                      {lead.last_outreach_replied_at && <span data-testid="engagement-replied" className="text-emerald-300 font-semibold">· REPLIED</span>}
                    </div>
                  )}
                </div>

                {lead.audit_share_token && !isNew && (
                  <AuditAnalyticsCard lead={lead} />
                )}

                <div className={`bg-zinc-900/60 border border-zinc-800 rounded-sm ${outreachLoading ? "ai-beam" : ""}`}>
                  <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800">
                    <div className="flex items-center gap-2 text-xs text-zinc-300"><LinkedinLogo size={14} /> LinkedIn</div>
                    <Button size="sm" variant="ghost" data-testid="outreach-li-copy" onClick={() => copy(lead.outreach_linkedin, "LinkedIn copy")} className="h-7 text-zinc-400 hover:text-zinc-100"><Copy size={12} className="mr-1" /> Copy</Button>
                  </div>
                  <pre data-testid="outreach-li-text" className="p-3 text-xs text-zinc-300 whitespace-pre-wrap font-mono min-h-[120px]">
                    {lead.outreach_linkedin || <span className="text-zinc-600">No LinkedIn copy yet.</span>}
                  </pre>
                </div>
              </TabsContent>

              <TabsContent value="proposals" className="flex-1 px-3 md:px-5 py-4">
                <ProposalsTab
                  lead={lead}
                  stripeConfigured={stripeConfigured}
                  onLeadUpdated={(updated) => {
                    setLead(updated);
                    onUpdated?.(updated);
                  }}
                />
              </TabsContent>

              <TabsContent value="history" className="flex-1 px-3 md:px-5 py-4" data-testid="history-tab-content">
                <HistoryTab
                  lead={lead}
                  onLeadUpdated={(updated) => {
                    setLead(updated);
                    onUpdated?.(updated);
                  }}
                />
              </TabsContent>
            </Tabs>

            <div className="sticky bottom-0 bg-zinc-950 border-t border-zinc-800 px-3 md:px-5 py-3 flex items-center justify-between">
              {!isNew ? (
                <Button data-testid="drawer-delete-btn" variant="ghost" onClick={() => setDeleteOpen(true)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                  <Trash size={14} className="mr-1.5" /> Delete
                </Button>
              ) : <div />}
              <div className="flex gap-2">
                <Button variant="outline" data-testid="drawer-cancel-btn" onClick={onClose} className="border-zinc-800 hover:bg-zinc-900 rounded-sm">Cancel</Button>
                <Button
                  data-testid="drawer-save-btn"
                  onClick={save}
                  disabled={saving}
                  className="bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm"
                >
                  {saving ? (<><Spinner size={14} className="mr-1.5 animate-spin" /> Saving…</>) : (<><FloppyDisk size={14} className="mr-1.5" /> {isNew ? "Create lead" : "Save"}</>)}
                </Button>
              </div>
            </div>
          </>
        )}
      </aside>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={`Delete ${lead?.business_name || "this lead"}?`}
        description="This permanently removes the lead and any pending Stripe proposals attached to it. This cannot be undone."
        confirmLabel="Delete lead"
        destructive
        onConfirm={del}
        testid="confirm-delete-lead"
      />

      <ConfirmDialog
        open={sendConfirmOpen}
        onOpenChange={setSendConfirmOpen}
        title={`Send outreach to ${lead?.email || "lead"}?`}
        description={`This will email "${lead?.business_name || "the business"}" via Resend from ${"info@clickforgeds.com"} and advance their stage to "First Outreach Sent".`}
        confirmLabel="Send email"
        onConfirm={sendEmail}
        testid="confirm-send-email"
      />
    </>
  );
}

function Field({ label, children, wide, testid }) {
  return (
    <div className={wide ? "col-span-2" : ""} data-testid={testid}>
      <Label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
